/**
 * Clause structure matching — canonical tokens → one grammatical reading, or a
 * teaching diagnostic (plan §3, M3).
 *
 * @remarks
 * Recognized patterns: `DIRECTION` | `VERB` | `VERB DIRECTION` | `VERB OBJ` |
 * `VERB PREP OBJ` | `VERB OBJ PREP OBJ`. Two positional-namespace rules do the
 * heavy lifting (M1 discovery 3):
 *
 *   - **Prepositions are judged per intent, post-verb.** A post-verb token is
 *     a preposition only when THIS intent declares it (`open ... with` binds a
 *     tool because `open` says so). A word some OTHER intent declares yields
 *     `unbound_preposition` — the parser never invents structure.
 *     Intent-declared prepositions outrank direction readings (`put card in
 *     slot` binds `in`; `go in` moves — `go` declares nothing).
 *   - **Directions demote to words inside phrases.** A direction token is a
 *     direction only where a direction can stand: alone (`north`), or as the
 *     sole post-verb token (`go north`). Anywhere else it is noun-phrase
 *     material (`inspect the north wall`).
 *
 * Bare directions bind `vocabulary.movementIntent` (authored
 * `movementIntentToken`, or the builtin fallback — D14). Diagnostics carry the
 * understood intent so consumers can teach in-world ("Take what?").
 * `objectPolicy` is enforced here: `required` without a direct object and
 * `forbidden` with one are diagnostics, not guesses.
 *
 * Fuzzy correction (M5) slots BEFORE this stage; entity resolution (M4)
 * consumes the phrases this stage carves out.
 *
 * @packageDocumentation
 */

import { type CompiledIntent } from '../lexicon/compile-lexicon.js';
import { type BoundPrepositionRole } from '../lexicon/lexicon-definition.js';
import { type SourceSpan } from '../types.js';
import {
	type CanonicalToken,
	type DirectionCanonicalToken,
	type IntentCanonicalToken,
	type PronounCanonicalToken,
	type WordCanonicalToken,
} from './canonicalize-tokens.js';
import { isNameMaterial } from './prepositions-inside-names.js';

/** One grammatical reading of a clause. Entity resolution (M4) consumes the phrases. */
export type ClauseStructure =
	| {
		/**
		 * D227: the thing the player named AFTER the direction (`go up THE
		 * ROAD`, `climb up THE STAIRS`) — destination color, resolved so a
		 * misnamed thing still fails honestly, and then set aside: the bearing
		 * is explicit, so the bearing is what executes (D31's rule for the bare
		 * `up the road`, which the shape ladder has read this way since R10).
		 * Absent on the plain `go up`.
		 */
		readonly destinationPhrase?: readonly PhraseToken[];
		readonly directionToken: DirectionCanonicalToken;
		readonly intentToken: IntentCanonicalToken;
		readonly kind: 'intent_direction';
	}
	| {
		readonly boundPhrase: readonly PhraseToken[];
		readonly intentToken: IntentCanonicalToken;
		readonly kind: 'intent_object_preposition';
		readonly objectPhrase: readonly PhraseToken[];
		/** D56: the phrasal verb's particle, wherever in the clause it landed. */
		readonly particle?: string;
		readonly preposition: PrepositionUse;
	}
	| {
		readonly boundPhrase: readonly PhraseToken[];
		readonly intentToken: IntentCanonicalToken;
		readonly kind: 'intent_preposition';
		/** D56: the phrasal verb's particle, wherever in the clause it landed. */
		readonly particle?: string;
		readonly preposition: PrepositionUse;
	}
	| {
		readonly directionToken: DirectionCanonicalToken;
		readonly intent: CompiledIntent;
		readonly kind: 'direction_only';
	}
	| {
		readonly intentToken: IntentCanonicalToken;
		readonly kind: 'intent_object';
		readonly objectPhrase: readonly PhraseToken[];
		/** D56: the phrasal verb's particle, wherever in the clause it landed. */
		readonly particle?: string;
	}
	| {
		readonly intentToken: IntentCanonicalToken;
		readonly kind: 'intent_only';
		/** D56: the phrasal verb's particle, wherever in the clause it landed. */
		readonly particle?: string;
	};

/** The outcome of matching one clause (result-typed; diagnostics are teaching data, not errors). */
export type ClauseStructureOutcome =
	| { readonly diagnostic: StructureDiagnostic; readonly type: 'unmatched' }
	| { readonly structure: ClauseStructure; readonly type: 'matched' };

/** Noun-phrase material: plain words and pronouns (directions demote into this). */
export type PhraseToken = PronounCanonicalToken | WordCanonicalToken;

/**
 * One recognized preposition use: the word, where it sat, and the role its
 * intent declared for it. D212: the ditransitive reading (`give sylvain the
 * compass`) binds the recipient with NO preposition typed, so `word` is
 * absent there and the span is the bound phrase's own.
 */
export interface PrepositionUse {
	readonly bindsAs: BoundPrepositionRole;
	readonly sourceSpan: SourceSpan;
	readonly word?: string;
}

/** A preposition the player TYPED — the only kind that can dangle, so the only kind the dangling diagnostic carries. */
export interface TypedPrepositionUse extends PrepositionUse {
	readonly word: string;
}

/**
 * Why a clause failed to match, with enough structure for in-world teaching.
 * M4's outcome builder maps these onto `ParseDiagnostics.failureKind`.
 */
export type StructureDiagnostic =
	| {
		/** The head intent in play when the structure fell apart (absent for bare-direction failures) — D24. */
		readonly intentToken?: IntentCanonicalToken;
		readonly kind: 'structure_not_understood';
		readonly offendingToken: CanonicalToken;
		readonly reason: StructureFailureReason;
	}
	| { readonly intentToken: IntentCanonicalToken; readonly kind: 'dangling_preposition'; readonly preposition: TypedPrepositionUse }
	| { readonly intentToken: IntentCanonicalToken; readonly kind: 'object_forbidden'; readonly objectPhrase: readonly PhraseToken[] }
	| { readonly intentToken: IntentCanonicalToken; readonly kind: 'object_required' }
	| {
		readonly intentToken: IntentCanonicalToken;
		readonly kind: 'unbound_preposition';
		readonly prepositionWord: string;
		readonly sourceSpan: SourceSpan;
	}
	| { readonly kind: 'empty_clause' }
	| { readonly kind: 'no_intent'; readonly leadingToken: CanonicalToken };

/** The documented reasons a clause can defeat the grammar outright. */
export type StructureFailureReason =
	| 'second_particle'
	| 'second_preposition_binding'
	| 'second_verb'
	| 'words_after_bare_direction';

/**
 * The narrow vocabulary port structure matching reads — `CompiledLexicon`
 * satisfies it structurally.
 */
export interface StructureVocabulary {
	/** What a bare direction parses as (authored movement intent or the builtin fallback). */
	readonly movementIntent: CompiledIntent;
	/** Every preposition declared by ANY intent — evidence for `unbound_preposition`. */
	readonly prepositionTokens: ReadonlySet<string>;
}

/**
 * Match one canonicalized clause against the recognized structures.
 *
 * @param tokens - The clause's canonical tokens (post-canonicalization; post-fuzzy once M5 lands)
 * @param vocabulary - The compiled vocabulary view (see {@link StructureVocabulary})
 * @returns The matched structure, or a teaching diagnostic
 */
export const matchClauseStructure = (
	tokens: readonly CanonicalToken[],
	vocabulary: StructureVocabulary,
): ClauseStructureOutcome => {
	const [leadingToken, ...restTokens] = tokens;

	if (leadingToken === undefined) {
		return unmatched({ kind: 'empty_clause' });
	}

	if (leadingToken.kind === 'direction') {
		const tokenAfterDirection = restTokens[0];

		if (tokenAfterDirection !== undefined) {
			return unmatched({
				kind: 'structure_not_understood',
				offendingToken: tokenAfterDirection,
				reason: 'words_after_bare_direction',
			});
		}

		return matched({ directionToken: leadingToken, intent: vocabulary.movementIntent, kind: 'direction_only' });
	}

	if (leadingToken.kind !== 'intent') {
		return unmatched({ kind: 'no_intent', leadingToken });
	}

	return matchMovementShape(leadingToken, restTokens, vocabulary) ?? matchVerbClause(leadingToken, restTokens, vocabulary);
};

/**
 * The movement readings of a verb clause (D227 gathers them in one place):
 *
 * - **VERB DIRECTION**, any verb: a direction may stand as the SOLE post-verb
 *   token — unless this intent declares its surface text as a preposition OR
 *   (D56) as a PARTICLE, either of which outranks the direction reading
 *   (positional namespace). The particle half was the omission that made
 *   `pick up` parse as `pick` + direction `up`: the particle was lost, and the
 *   return also skipped `objectPolicy`, so a required-object verb came back
 *   objectless. Harmless in the shipped vocabulary only because `turn`'s
 *   particles are `off`/`on` and neither is a direction token — i.e. it broke
 *   for the second pack, which is the case engineering_standards §2.1 says to
 *   design for.
 * - **A verb that stands for a direction** (D227, `bareDirection`): alone, it
 *   IS that direction (`climb` → up); with a direction, the direction typed
 *   (`climb down`). Either way the clause is the MOVEMENT intent's — the
 *   verb's own mechanics, if it has any, are for the thing it is aimed at.
 * - **MOVEMENT-VERB DIRECTION THING** (D227, Calder's `the-road-climbs` §3):
 *   `go up the road`, `walk up the road`, `climb up the stairs`. The words
 *   after the direction are destination color the resolver must place; the
 *   bearing executes. Only a movement-shaped verb reads this way — `inspect
 *   the north wall` keeps its wall.
 *
 * @param intentToken - The clause's leading intent
 * @param postVerbTokens - Everything after the verb
 * @param vocabulary - The compiled vocabulary view
 * @returns The movement structure, or `undefined` when this clause is not one
 */
const matchMovementShape = (
	intentToken: IntentCanonicalToken,
	postVerbTokens: readonly CanonicalToken[],
	vocabulary: StructureVocabulary,
): ClauseStructureOutcome | undefined => {
	const { bareDirection } = intentToken.intent;
	const isMovementShaped = bareDirection !== undefined || intentToken.intent === vocabulary.movementIntent;
	// A verb standing for a direction, with nothing after it, is the direction.
	const asMovement: IntentCanonicalToken =
		bareDirection === undefined ? intentToken : { ...intentToken, intent: vocabulary.movementIntent, wasAliasMatched: true };

	if (bareDirection !== undefined && postVerbTokens.length === 0) {
		return matched({
			directionToken: { direction: bareDirection, kind: 'direction', sourceSpan: intentToken.sourceSpan, sourceText: intentToken.sourceText, wasAliasMatched: true },
			intentToken: asMovement,
			kind: 'intent_direction',
		});
	}

	const [firstToken, ...destinationTokens] = postVerbTokens;

	if (
		firstToken === undefined ||
		firstToken.kind !== 'direction' ||
		intentToken.intent.prepositionRoles.get(firstToken.sourceText) !== undefined ||
		intentToken.intent.particles.has(firstToken.sourceText)
	) {
		return undefined;
	}

	if (destinationTokens.length === 0) {
		return matched({ directionToken: firstToken, intentToken: asMovement, kind: 'intent_direction' });
	}

	// A second verb among the words is still a second verb (the D21 demotion
	// decides what it was); a verb that is not movement-shaped keeps its
	// phrase, direction word and all (`inspect the north wall`).
	const destinationPhrase = isMovementShaped ? phraseTokensOf(destinationTokens) : undefined;

	return destinationPhrase === undefined
		? undefined
		: matched({ destinationPhrase, directionToken: firstToken, intentToken: asMovement, kind: 'intent_direction' });
};

/**
 * Read a run of tokens as noun-phrase material: mid-phrase directions demote
 * to words (`north gate`), as they do everywhere, and a verb among them means
 * the run is not a phrase at all.
 *
 * @param tokens - The tokens after the bearing
 * @returns The phrase, or `undefined` when a verb stands in it
 */
const phraseTokensOf = (tokens: readonly CanonicalToken[]): undefined | readonly PhraseToken[] => {
	const phrase: PhraseToken[] = [];

	for (const token of tokens) {
		if (token.kind === 'intent') {
			return undefined;
		}

		phrase.push(token.kind === 'direction' ? { kind: 'word', sourceSpan: token.sourceSpan, sourceText: token.sourceText } : token);
	}

	return phrase;
};

/**
 * Wrap a structure as a matched outcome.
 *
 * @param structure - The matched structure
 * @returns The outcome
 */
const matched = (structure: ClauseStructure): ClauseStructureOutcome => ({ structure, type: 'matched' });

/**
 * Walk a verb clause's post-verb tokens into direct/bound phrases around at
 * most one intent-declared preposition, then enforce the intent's policies.
 *
 * @param intentToken - The clause's leading intent
 * @param postVerbTokens - Everything after the verb
 * @param vocabulary - The compiled vocabulary view
 * @returns The matched structure, or a teaching diagnostic
 */
const matchVerbClause = (
	intentToken: IntentCanonicalToken,
	postVerbTokens: readonly CanonicalToken[],
	vocabulary: StructureVocabulary,
): ClauseStructureOutcome => {
	const boundPhrase: PhraseToken[] = [];
	const objectPhrase: PhraseToken[] = [];

	let preposition: TypedPrepositionUse | undefined;
	let particle: string | undefined;

	const activePhrase = (): PhraseToken[] => (preposition === undefined ? objectPhrase : boundPhrase);

	for (const [tokenIndex, token] of postVerbTokens.entries()) {
		if (token.kind === 'intent') {
			return unmatched({ intentToken, kind: 'structure_not_understood', offendingToken: token, reason: 'second_verb' });
		}

		/*
		 * D56: the particle check comes FIRST, and deliberately. "on" is a
		 * declared preposition for `push`, so without this ordering `turn the
		 * kettle on` dies as an unbound preposition — which is exactly the wrong
		 * answer it used to give. The intent in play is the authority on what its
		 * own words are doing.
		 *
		 * Position IS checked, and the two legal positions are the whole of the
		 * grammar: adjacent to the verb ("turn on the kettle") or trailing after
		 * the object ("turn the kettle on", and with a pronoun ONLY this one —
		 * "turn it on"). A particle cannot sit mid-phrase.
		 *
		 * The first draft accepted any position and quietly broke every locative:
		 * "turn the dial on the wall" bound `on` as a particle, glued "dial" and
		 * "wall" into one object phrase, and answered that it could not find the
		 * dial — for a construction players type constantly, and where the old
		 * `unbound_preposition` teach had named the real problem.
		 */
		if (isParticleHere(intentToken, token, tokenIndex, postVerbTokens.length)) {
			if (particle !== undefined) {
				return unmatched({
					intentToken,
					kind: 'structure_not_understood',
					offendingToken: token,
					reason: 'second_particle',
				});
			}

			particle = token.sourceText;
			continue;
		}

		// D242: a declared preposition inside one of the catalog's names is the name's word (`take the coin from home`).
		const declaredRole = isNameMaterial(token) ? undefined : intentToken.intent.prepositionRoles.get(token.sourceText);

		// D229: a preposition that INTRODUCES the object (`hack at the tree`)
		// is swallowed, and the words after it are the object. Anywhere but
		// directly before the object — after one, or after a binding — it is
		// the unbound word it always was, and the teach names it.
		if (declaredRole === 'direct_object') {
			if (objectPhrase.length > 0 || preposition !== undefined) {
				return unmatched({ intentToken, kind: 'unbound_preposition', prepositionWord: token.sourceText, sourceSpan: token.sourceSpan });
			}

			continue;
		}

		if (declaredRole !== undefined) {
			if (preposition !== undefined) {
				return unmatched({
					intentToken,
					kind: 'structure_not_understood',
					offendingToken: token,
					reason: 'second_preposition_binding',
				});
			}

			preposition = { bindsAs: declaredRole, sourceSpan: token.sourceSpan, word: token.sourceText };
			continue;
		}

		if (token.kind === 'direction') {
			// Mid-phrase directions are noun-phrase material ("north wall").
			activePhrase().push({ kind: 'word', sourceSpan: token.sourceSpan, sourceText: token.sourceText });
			continue;
		}

		if (token.kind === 'word' && !isNameMaterial(token) && vocabulary.prepositionTokens.has(token.sourceText)) {
			/*
			 * R35 (D115; Calder's §6.1): for a DECLARED-UNSUPPORTED intent, the
			 * verb is the whole problem, and the nearest barrier speaks first —
			 * "PRY has no use for 'with' in that position" was parser snark over
			 * the engine's authored unsupported-verb teach, so `pry the grille
			 * with the pry bar` taught grammar while `pry grille` taught the
			 * world. The clause matches with the object carved out so far; the
			 * tail is moot because the engine refuses on the verb regardless,
			 * naming the OBJECT's authored voice — which is the one that knows
			 * what the player was trying to do.
			 */
			if (intentToken.intent.isUnsupported) {
				break;
			}

			return unmatched({
				intentToken,
				kind: 'unbound_preposition',
				prepositionWord: token.sourceText,
				sourceSpan: token.sourceSpan,
			});
		}

		activePhrase().push(token);
	}

	// A typed preposition with nothing after it dangles — judged here, where
	// the word is known to have been typed; the ditransitive below binds a
	// recipient phrase and so can never dangle.
	if (preposition !== undefined && boundPhrase.length === 0) {
		return unmatched({ intentToken, kind: 'dangling_preposition', preposition });
	}

	return settleVerbClause(intentToken, readDitransitive(intentToken, { boundPhrase, objectPhrase, particle, preposition }));
};

/**
 * D212: the ditransitive — `give sylvain the compass`, `show the warden the
 * pass` — read as two phrases where the verb declares an `indirect_object`
 * role and an article marks where the second phrase starts (D60's trace).
 * The phrase before the article is the recipient, the phrase from it the
 * object. Only that shape: no article, no split — `give sylvain compass`
 * is one phrase and fails as it always has, because the alternative is
 * guessing where one noun ends and the next begins.
 *
 * @param intentToken - The clause's leading intent
 * @param walked - The walk's accumulated phrases
 * @returns The walk, re-carved when the ditransitive shape is present
 */
const readDitransitive = (intentToken: IntentCanonicalToken, walked: WalkedVerbClause): WalkedVerbClause => {
	const hasRecipientRole = [...intentToken.intent.prepositionRoles.values()].includes('indirect_object');

	if (walked.preposition !== undefined || !hasRecipientRole) {
		return walked;
	}

	const secondPhraseStart = walked.objectPhrase.findIndex(
		(token, index) => index > 0 && token.kind === 'word' && token.isNounPhraseStart === true,
	);

	if (secondPhraseStart === -1) {
		return walked;
	}

	const boundPhrase = walked.objectPhrase.slice(0, secondPhraseStart);

	return {
		boundPhrase,
		objectPhrase: walked.objectPhrase.slice(secondPhraseStart),
		particle: walked.particle,
		preposition: {
			bindsAs: 'indirect_object',
			// The recipient phrase's own extent — the whole of what stands in for the preposition.
			sourceSpan: {
				end: Math.max(...boundPhrase.map((token) => token.sourceSpan.end)),
				start: Math.min(...boundPhrase.map((token) => token.sourceSpan.start)),
			},
		},
	};
};

/** The walked pieces of a verb clause, ready for policy enforcement. */
interface WalkedVerbClause {
	readonly boundPhrase: readonly PhraseToken[];
	readonly objectPhrase: readonly PhraseToken[];
	readonly particle: string | undefined;
	readonly preposition: PrepositionUse | undefined;
}

/**
 * Enforce the intent's policies over a walked clause and build the structure
 * (split from the walk itself at the complexity cap, R35 — the walk carves,
 * this judges).
 *
 * @param intentToken - The clause's leading intent
 * @param walked - The walk's accumulated phrases, particle and preposition
 * @returns The matched structure, or a teaching diagnostic
 */
const settleVerbClause = (intentToken: IntentCanonicalToken, walked: WalkedVerbClause): ClauseStructureOutcome => {
	const { boundPhrase, objectPhrase, particle, preposition } = walked;

	if (objectPhrase.length > 0 && intentToken.intent.objectPolicy === 'forbidden') {
		return unmatched({ intentToken, kind: 'object_forbidden', objectPhrase });
	}

	if (objectPhrase.length === 0 && intentToken.intent.objectPolicy === 'required') {
		return unmatched({ intentToken, kind: 'object_required' });
	}

	// Absent rather than present-and-undefined, matching how every other optional
	// field on the structure and on `ParsedCommand` is built. `toEqual` and this
	// repo's tsconfig both treat the two as the same today, so this is
	// consistency rather than correctness — but a command whose shape depends on
	// which branch built it is the kind of thing determinism work later trips
	// over.
	const particleField = particle === undefined ? {} : { particle };

	if (preposition !== undefined) {
		return objectPhrase.length > 0
			? matched({ boundPhrase, intentToken, kind: 'intent_object_preposition', objectPhrase, preposition, ...particleField })
			: matched({ boundPhrase, intentToken, kind: 'intent_preposition', preposition, ...particleField });
	}

	return objectPhrase.length > 0
		? matched({ intentToken, kind: 'intent_object', objectPhrase, ...particleField })
		: matched({ intentToken, kind: 'intent_only', ...particleField });
};

/**
 * Is this token a particle of the clause's verb, in a position a particle may
 * legally occupy (D56)?
 *
 * @remarks
 * The two legal positions are the whole of the grammar: adjacent to the verb
 * ("turn ON the kettle") or trailing after the object phrase ("turn the kettle
 * ON", and with a pronoun only this one — "turn it ON"). Anywhere between is a
 * PREPOSITION, and treating it as a particle glues the phrases either side into
 * one object — which is how the first draft answered "no such dial" to "turn the
 * dial on the wall".
 *
 * @param intentToken - The clause's leading intent
 * @param token - The token under consideration
 * @param tokenIndex - Its index among the post-verb tokens
 * @param tokenCount - How many post-verb tokens there are
 * @returns Whether to bind it as this clause's particle
 */
const isParticleHere = (
	intentToken: IntentCanonicalToken,
	token: CanonicalToken,
	tokenIndex: number,
	tokenCount: number,
): boolean =>
	(tokenIndex === 0 || tokenIndex === tokenCount - 1) && intentToken.intent.particles.has(token.sourceText);

/**
 * Wrap a diagnostic as an unmatched outcome.
 *
 * @param diagnostic - The teaching diagnostic
 * @returns The outcome
 */
const unmatched = (diagnostic: StructureDiagnostic): ClauseStructureOutcome => ({ diagnostic, type: 'unmatched' });
